/**
 * 「🧪 체험용 테스트 데이터가 들어 있습니다 · 한 번에 지우기」 띠 (2026-09-30).
 *
 * 캠페인 고객에게 계정을 열면 그 매장에 모든 기능을 한 번씩 써 볼 수 있는 가짜 데이터가 깔린다
 * (이름 옆 «(테스트)»). 실제 고객을 받기 시작하면 그 가짜들이 섞여 헷갈리므로, **어느 화면에 있든
 * 맨 위에서** 한 번에 치울 수 있게 한다.
 *  · **매장대표에게만** 보인다 — 매장 데이터를 통째로 지우는 버튼이라 직원에게 주지 않는다.
 *  · 화면을 열 때 한 번만 묻는다(NewPlansBanner 와 같은 관례 — 저녁 요청이 몰리는 서비스다).
 *  · 지우면 매장 전체 캐시를 비워, 목록에서 테스트 고객이 바로 사라지게 한다.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { useConfirm } from "../components/common/ConfirmDialog";
import { clearSampleData, fetchSampleDataStatus } from "../api/sampleData";

const LABEL: Record<string, string> = {
  customers: "고객",
  application_forms: "신청서",
  form_templates: "서식",
  text_templates: "텍스트 변환 문구",
  inventory_devices: "재고",
  policy_face_uploads: "샘플 정책표",
  dashboard_memos: "메모",
};

export default function SampleDataBanner() {
  const { isStoreManager } = useAuth();
  const enabled = isStoreManager();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const statusQ = useQuery({
    queryKey: ["sample-data"],
    queryFn: fetchSampleDataStatus,
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
  const clear = useMutation({
    mutationFn: clearSampleData,
    onSuccess: () => {
      // 고객·재고·서식 목록이 전부 바뀐다 — 화면별 키를 하나하나 고르지 않고 통째로 새로 읽는다.
      void qc.invalidateQueries();
    },
  });

  const total = statusQ.data?.total ?? 0;
  if (!enabled || total === 0) return null;

  const summary = Object.entries(statusQ.data?.counts ?? {})
    .filter(([, n]) => n > 0)
    .map(([k, n]) => `${LABEL[k] ?? k} ${n}`)
    .join(" · ");

  async function onClear() {
    // 지우면 되돌릴 수 없다 — 먼저 둘러보기를 권하는 안내창(2026-09-30 사용자 요청).
    const ok = await confirm({
      title: "테스트 데이터를 지우기 전에",
      message: (
        <div className="space-y-3">
          <p className="font-semibold text-rose-600 dark:text-rose-400">
            지운 테스트 데이터는 다시 복구할 수 없습니다.
          </p>
          <p>
            샘플이 남아 있는 동안 아래 기능을 한 번씩 둘러보신 뒤에 지우시길 추천드립니다.
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>고객 관리 — 개통대기 현황 단계 넘기기, 신청서 열어 보기</li>
            <li>서류 자동작성 — 테스트 고객으로 통신사 신청서 채워 보기</li>
            <li>텍스트 자동변환 — 신청서 값으로 문구 만들어 보기</li>
            <li>가망고객 · 개통완료 · 정산완료 · 재고 화면</li>
          </ul>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            지울 것: {summary}. 직접 입력한 실제 데이터는 그대로 남습니다.
          </p>
        </div>
      ),
      cancelText: "더 둘러보기",
      confirmText: "그래도 지우기",
      tone: "danger",
    });
    if (ok) clear.mutate();
  }

  return (
    <div
      className="flex items-center gap-3 border-b border-sky-300 bg-sky-50 px-4 py-2 text-sm text-sky-900 dark:border-sky-700 dark:bg-sky-950 dark:text-sky-100"
      role="status"
      data-testid="sample-data-banner"
    >
      <span className="font-semibold">🧪 체험용 테스트 데이터가 들어 있습니다</span>
      <span className="hidden truncate text-sky-800 dark:text-sky-200 sm:inline">
        이름 옆 «(테스트)» — {summary}
      </span>
      <button
        type="button"
        onClick={() => void onClear()}
        disabled={clear.isPending}
        className="ml-auto shrink-0 rounded-lg bg-sky-600 px-3 py-1 font-semibold text-white hover:bg-sky-700 disabled:opacity-50"
      >
        {clear.isPending ? "지우는 중" : "테스트 데이터 한 번에 지우기"}
      </button>
    </div>
  );
}
